import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ShieldCheck, KeyRound, Users, X, Check, Power } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";
const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const STATUS_CLS = { active: "bg-emerald-100 text-emerald-700", trial: "bg-sky-100 text-sky-700", expired: "bg-rose-100 text-rose-700", pending_payment: "bg-amber-100 text-amber-700", suspended: "bg-rose-100 text-rose-700" };
const PAY_STATUS_CLS = { success: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700" };
const PAY_STATUS_LABEL = { success: "Berhasil", pending: "Pending", failed: "Gagal" };
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

export default function AdminSubscriptionPage() {
  const [tab, setTab] = useState("owners");
  const [settings, setSettings] = useState(null);
  const [duitku, setDuitku] = useState({ merchant_code: "", api_key: "", env: "sandbox" });
  const [plans, setPlans] = useState([]);
  const [planForm, setPlanForm] = useState(null);
  const [owners, setOwners] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [payments, setPayments] = useState([]);
  const [paySummary, setPaySummary] = useState(null);
  const [payFilter, setPayFilter] = useState("all");
  const [payQuery, setPayQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get("/subscription/admin/settings").then((r) => setSettings(r.data)),
      apiClient.get("/subscription/admin/plans").then((r) => setPlans(r.data)),
      apiClient.get("/subscription/admin/owners/stats").then((r) => setStats(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const loadOwners = useCallback(() => {
    apiClient.get(`/subscription/admin/owners${filter !== "all" ? `?status=${filter}` : ""}`).then((r) => setOwners(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { loadOwners(); }, [loadOwners]);

  const loadPayments = useCallback(() => {
    const params = new URLSearchParams();
    if (payFilter !== "all") params.set("status", payFilter);
    if (payQuery.trim()) params.set("q", payQuery.trim());
    apiClient.get(`/subscription/admin/payments?${params.toString()}`).then((r) => { setPayments(r.data.payments); setPaySummary(r.data.summary); }).catch(() => {});
  }, [payFilter, payQuery]);
  useEffect(() => { if (tab === "payments") loadPayments(); }, [tab, loadPayments]);

  const saveSettings = async () => {
    try {
      await apiClient.put("/subscription/admin/settings", {
        enabled: settings.enabled, trial_enabled: settings.trial_enabled,
        trial_days: Number(settings.trial_days), warning_days: settings.warning_days,
      });
      toast.success("Pengaturan disimpan.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const saveDuitku = async () => {
    if (!duitku.merchant_code || !duitku.api_key) { toast.error("Isi Merchant Code & API Key."); return; }
    try {
      const { data } = await apiClient.put("/subscription/admin/platform-duitku", duitku);
      setSettings(data); setDuitku({ merchant_code: "", api_key: "", env: data.platform_duitku.env });
      toast.success("Kredensial Duitku subscription disimpan.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const savePlan = async () => {
    const body = { name: planForm.name, description: planForm.description, duration_days: Number(planForm.duration_days), price: Number(planForm.price), status: planForm.status, sort_order: Number(planForm.sort_order || 0) };
    try {
      if (planForm.id) await apiClient.put(`/subscription/admin/plans/${planForm.id}`, body);
      else await apiClient.post("/subscription/admin/plans", body);
      toast.success("Plan disimpan."); setPlanForm(null);
      apiClient.get("/subscription/admin/plans").then((r) => setPlans(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Hapus plan ini?")) return;
    await apiClient.delete(`/subscription/admin/plans/${id}`);
    apiClient.get("/subscription/admin/plans").then((r) => setPlans(r.data));
    toast.success("Plan dihapus.");
  };

  const override = async (ownerId, payload) => {
    try {
      await apiClient.post(`/subscription/admin/owners/${ownerId}/override`, payload);
      toast.success("Subscription owner diperbarui.");
      loadOwners(); apiClient.get("/subscription/admin/owners/stats").then((r) => setStats(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (loading || !settings) return <AdminLayout testid="admin-subscription-page"><div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div></AdminLayout>;

  const tabs = [{ k: "owners", label: "Owner" }, { k: "payments", label: "Transaksi" }, { k: "plans", label: "Paket" }, { k: "settings", label: "Pengaturan" }];

  return (
    <AdminLayout testid="admin-subscription-page">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck size={22} className="text-primary" /><h1 className="font-heading text-3xl font-bold tracking-tight">Subscription</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Atur trial, paket, kredensial pembayaran, dan pantau subscription setiap Owner.</p>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="subscription-stats">
          {[["Total", stats.total], ["Aktif", stats.active], ["Trial", stats.trial], ["Segera Habis", stats.expiring_soon], ["Expired", stats.expired], ["Pending", stats.pending_payment]].map(([l, v]) => (
            <div key={l} className="bg-card border border-border rounded-xl p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="text-xl font-bold">{v}</p></div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-5 border-b border-border">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`sub-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>{t.label}</button>
        ))}
      </div>

      {/* OWNERS */}
      {tab === "owners" && (
        <div>
          <div className="flex gap-2 flex-wrap mb-4">
            {["all", "active", "trial", "expired", "pending_payment", "suspended"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`owner-filter-${f}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>{f}</button>
            ))}
          </div>
          <div className="space-y-2" data-testid="owner-subscription-list">
            {owners.map((o) => (
              <div key={o.owner_id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`owner-sub-${o.owner_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{o.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{o.email}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${STATUS_CLS[o.status] || "bg-secondary"}`}>{o.status}</span>
                <span className="text-xs text-muted-foreground w-28">{o.days_left != null ? `${o.days_left} hari lagi` : "—"}</span>
                <div className="flex gap-1.5">
                  <button onClick={() => override(o.owner_id, { action: "extend", days: 30 })} data-testid={`owner-extend-${o.owner_id}`} className="px-2.5 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60">+30 hari</button>
                  {o.status === "suspended"
                    ? <button onClick={() => override(o.owner_id, { action: "set_status", status: "active" })} className="px-2.5 py-1.5 rounded-full border border-emerald-200 text-emerald-600 text-xs font-semibold">Aktifkan</button>
                    : <button onClick={() => override(o.owner_id, { action: "set_status", status: "suspended" })} data-testid={`owner-suspend-${o.owner_id}`} className="px-2.5 py-1.5 rounded-full border border-rose-200 text-rose-600 text-xs font-semibold inline-flex items-center gap-1"><Power size={12} /> Suspend</button>}
                </div>
              </div>
            ))}
            {owners.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada owner.</p>}
          </div>
        </div>
      )}

      {/* PAYMENTS / TRANSACTIONS */}
      {tab === "payments" && (
        <div data-testid="payments-tab">
          {paySummary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="payments-summary">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 col-span-2 sm:col-span-1">
                <p className="text-xs text-emerald-700">Total Pemasukan</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="payments-total-income">{rupiah(paySummary.total_income)}</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4"><p className="text-xs text-muted-foreground">Berhasil</p><p className="text-lg font-bold text-emerald-600">{paySummary.success || 0}</p></div>
              <div className="bg-card border border-border rounded-xl p-4"><p className="text-xs text-muted-foreground">Pending</p><p className="text-lg font-bold text-amber-600">{paySummary.pending || 0}</p></div>
              <div className="bg-card border border-border rounded-xl p-4"><p className="text-xs text-muted-foreground">Gagal</p><p className="text-lg font-bold text-rose-600">{paySummary.failed || 0}</p></div>
            </div>
          )}
          <div className="flex gap-2 flex-wrap mb-3 items-center">
            {[["all", "Semua"], ["success", "Berhasil"], ["pending", "Pending"], ["failed", "Gagal"]].map(([f, lbl]) => (
              <button key={f} onClick={() => setPayFilter(f)} data-testid={`payment-filter-${f}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${payFilter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>{lbl}</button>
            ))}
            <input value={payQuery} onChange={(e) => setPayQuery(e.target.value)} placeholder="Cari nama / email owner…" data-testid="payment-search" className="ml-auto w-full sm:w-64 rounded-full border border-border bg-background px-4 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="space-y-2" data-testid="payments-list">
            {payments.map((p) => (
              <div key={p.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`payment-row-${p.order_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{p.owner_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.owner_email}</p>
                  <p className="text-[11px] text-muted-foreground/80 font-mono truncate">{p.order_id}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{rupiah(p.amount)}</p>
                  <p className="text-xs text-muted-foreground">{p.plan_name}</p>
                </div>
                <div className="w-28 text-xs text-muted-foreground">
                  <p>{p.payment_method || "—"}</p>
                  <p>{fmtDate(p.created_at)}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${PAY_STATUS_CLS[p.status] || "bg-secondary"}`}>{PAY_STATUS_LABEL[p.status] || p.status}</span>
              </div>
            ))}
            {payments.length === 0 && <p className="text-sm text-muted-foreground">Belum ada transaksi.</p>}
          </div>
        </div>
      )}

      {/* PLANS */}
      {tab === "plans" && (
        <div>
          <button onClick={() => setPlanForm({ name: "", description: "", duration_days: 30, price: 0, status: "active", sort_order: 0 })} data-testid="plan-add-button" className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Plus size={15} /> Tambah Paket</button>
          <div className="space-y-2" data-testid="plans-list">
            {plans.map((p) => (
              <div key={p.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`plan-row-${p.id}`}>
                <div className="min-w-0 flex-1"><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.duration_days} hari · {rupiah(p.price)}</p></div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{p.status}</span>
                <button onClick={() => setPlanForm(p)} data-testid={`plan-edit-${p.id}`} className="px-3 py-1.5 rounded-full border border-border text-xs font-semibold">Edit</button>
                <button onClick={() => deletePlan(p.id)} className="px-2.5 py-1.5 rounded-full border border-rose-200 text-rose-600 text-xs font-semibold"><Trash2 size={13} /></button>
              </div>
            ))}
            {plans.length === 0 && <p className="text-sm text-muted-foreground">Belum ada paket.</p>}
          </div>
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && (
        <div className="space-y-6 max-w-2xl">
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <h2 className="font-heading text-lg font-semibold">Pengaturan Umum</h2>
            <label className="flex items-center justify-between"><span className="text-sm">Aktifkan Subscription Owner</span>
              <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} data-testid="settings-enabled" className="w-5 h-5 accent-primary" /></label>
            <label className="flex items-center justify-between"><span className="text-sm">Aktifkan Trial</span>
              <input type="checkbox" checked={settings.trial_enabled} onChange={(e) => setSettings({ ...settings, trial_enabled: e.target.checked })} data-testid="settings-trial-enabled" className="w-5 h-5 accent-primary" /></label>
            <div><label className="block text-xs text-muted-foreground mb-1">Durasi Trial (hari)</label>
              <input type="number" min={0} value={settings.trial_days} onChange={(e) => setSettings({ ...settings, trial_days: e.target.value })} className={inputCls} data-testid="settings-trial-days" /></div>
            <div><label className="block text-xs text-muted-foreground mb-1">Hari Peringatan (pisah koma)</label>
              <input value={(settings.warning_days || []).join(", ")} onChange={(e) => setSettings({ ...settings, warning_days: e.target.value.split(",").map((x) => parseInt(x.trim())).filter((n) => !isNaN(n)) })} className={inputCls} data-testid="settings-warning-days" /></div>
            <button onClick={saveSettings} data-testid="settings-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold"><Save size={16} /> Simpan Pengaturan</button>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2"><KeyRound size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Payment Gateway Subscription (Duitku Platform)</h2></div>
            <p className="text-xs text-muted-foreground -mt-2">Kredensial Duitku milik AVASA, khusus untuk tagihan subscription owner (terpisah dari Duitku owner).</p>
            {settings.platform_duitku?.configured
              ? <p className="text-sm">Status: <span className="font-semibold text-emerald-600">Terkonfigurasi</span> · Merchant <span className="font-mono">{settings.platform_duitku.merchant_code}</span> · Key ••••{settings.platform_duitku.api_key_last4} · Mode {settings.platform_duitku.env}</p>
              : <p className="text-sm text-amber-600">Belum dikonfigurasi. Owner belum bisa membayar subscription sampai diisi.</p>}
            <div className="flex gap-2">
              {["sandbox", "production"].map((v) => (
                <button key={v} onClick={() => setDuitku({ ...duitku, env: v })} data-testid={`sub-duitku-env-${v}`} className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold border ${duitku.env === v ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{v}</button>
              ))}
            </div>
            <input value={duitku.merchant_code} onChange={(e) => setDuitku({ ...duitku, merchant_code: e.target.value })} placeholder="Merchant Code" className={inputCls} data-testid="sub-duitku-merchant" />
            <input type="password" value={duitku.api_key} onChange={(e) => setDuitku({ ...duitku, api_key: e.target.value })} placeholder="API Key" className={inputCls} data-testid="sub-duitku-apikey" />
            <button onClick={saveDuitku} data-testid="sub-duitku-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold"><Save size={16} /> Simpan Kredensial</button>
          </div>
        </div>
      )}

      {/* Plan modal */}
      {planForm && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="plan-modal">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-3">
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">{planForm.id ? "Edit Paket" : "Paket Baru"}</h3><button onClick={() => setPlanForm(null)}><X size={16} /></button></div>
            <input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Nama paket" className={inputCls} data-testid="plan-name" />
            <input value={planForm.description || ""} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} placeholder="Deskripsi" className={inputCls} data-testid="plan-desc" />
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs text-muted-foreground mb-1">Durasi (hari)</label><input type="number" value={planForm.duration_days} onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value })} className={inputCls} data-testid="plan-duration" /></div>
              <div><label className="block text-xs text-muted-foreground mb-1">Harga (Rp)</label><input type="number" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} className={inputCls} data-testid="plan-price" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs text-muted-foreground mb-1">Urutan</label><input type="number" value={planForm.sort_order} onChange={(e) => setPlanForm({ ...planForm, sort_order: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs text-muted-foreground mb-1">Status</label>
                <select value={planForm.status} onChange={(e) => setPlanForm({ ...planForm, status: e.target.value })} className={inputCls} data-testid="plan-status"><option value="active">active</option><option value="inactive">inactive</option></select></div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPlanForm(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold">Batal</button>
              <button onClick={savePlan} data-testid="plan-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Check size={15} /> Simpan</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
